// ============================================================================
// SOP Create modal — Trello-cutover rebuild (2026-05-29).
//
// Folds in cutover items 4/5/6/7/8/9:
//   * #9 Sections by owner: Client & Billing (BDD) · Square Footage (BDD+AM) ·
//        Contractor (AM) · Notes. Client Monthly Price and Monthly Sub Payment
//        now live in DIFFERENT sections so they're never confused.
//   * #5 BDD / AM pickers bound to employees.is_bdd_eligible / is_am_eligible
//        (+ employment_status='Active') — no cleaners/part-timers/Former.
//   * #6 Pricing rename (monthly_payment → sub_payment) + relabel + a margin
//        slider that derives sub_payment from final_pricing × (1 − margin%).
//   * #7 Three-way subcontractor selector (Previous / New / Unassigned),
//        Previous filtered to active subs in the card's market.
//   * #8 Choosing "New" routes through the one canonical sub-onboarding helper.
//   * #4 Draft autosave: once ~4 fields are filled we INSERT an is_draft row and
//        debounce-update it; "Create account" flips is_draft=false. Drafts are
//        resumable from any device (see the Drafts list in index.tsx).
//
// Mobile-first: full-screen sheet < sm, centered dialog ≥ sm. Required fields
// sit above the fold; the owner-sections expand below.
// ============================================================================

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import {
  NAS_FACILITY_TYPE_LABELS,
  NAS_FACILITY_TYPE_OPTIONS,
  NAS_LEGACY_MARKET_MAP,
  type NewAccountSop,
  type NewAccountSopFacilityType,
  type NewAccountSopPriority,
} from '../../shared/types';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { MARKET_STATE_MISMATCH_HINT } from '../../shared/errors';
import {
  useMarkets,
  marketOptions,
  marketColorPair,
  marketCoversState,
} from '../../shared/markets';
// AFB-0041 — usables tri-state shared with the facility card, so intake and
// edit cannot drift apart on what "not set" means.
import {
  USABLES_OPTIONS,
  toUsablesChoice,
  fromUsablesChoice,
} from '../../shared/facility-billing';
import { createSubForBuilding, newSubKey } from './sub-onboarding';
import { AlreadyOnFileNotice, alreadyOnFileFrom, type AlreadyOnFileError } from '../subcontractors/already-on-file';
import { ServiceLineCard } from './service-line-card';
import {
  emptyServiceLine,
  isMeaningfulLine,
  serviceLineTotals,
  serviceLinesFromRow,
  serviceLineToPatch,
  type ServiceLineForm,
} from './lib/service-lines';
import { toNumOrNull } from '../../shared/coerce';
import { overlayClickClose } from '../../shared/overlay-close';
import {
  DEFAULT_MGMT_FEE_PCT,
  feePctToFraction,
  feeStateFromFraction,
  mgmtFeeToPersist,
} from './mgmt-fee';
import {
  canAdvance,
  blockingReasons,
  nextStep,
  prevStep,
  stepRail,
  stepHeading,
  stepSubheading,
  reviewFooterSentence,
  FACILITY_NAME_HELP,
  CUSTOMER_HELP,
  BILL_TO_HELP,
  type IntakeStep,
} from './intake-steps';
import { StepRail, IntakeField, FieldHelp, ReviewRow, Field, INPUT_CLS, INTAKE_INPUT_CLS } from './intake-parts';
import CustomerPicker, { type CustomerChoice } from './customer-picker';

interface SopCreateModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called once the new SOP is persisted (is_draft=false). Parent may refresh
   * the board, toast, or open the detail drawer.
   */
  onCreated?: (sopId: string, accountName: string) => void;
  /**
   * When present the modal resumes an existing draft row instead of starting
   * blank — drives the "Resume" affordance in the Drafts list (#4).
   */
  resumeDraft?: NewAccountSop | null;
}

interface EmployeeOption {
  employee_id: string;
  name: string;
}

interface FormState {
  // Step 1 — the building, asked FIRST (Ben 2026-08-25). The form never used to ask
  // for this at all, which is why 10 of 38 cards carried no facility name and the
  // card fell back to a Trello "card title".
  facility_name: string;
  // Step 2 — who signs. account_id is set by CHOOSING from the picker; it outranks
  // the typed name in materialize_new_account_sop, so the choice is authoritative.
  account_id: string;
  account_parent_id: string;
  account_is_new: boolean;
  // Step 2 — who pays, when that is not the customer. Lands on
  // facilities.billing_account_id after the card materializes.
  billing_account_id: string;
  // Client & Billing (BDD)
  account_name: string;
  facility_type: NewAccountSopFacilityType | '';
  market_region: string;
  start_date: string;
  priority: NewAccountSopPriority;
  bdd_eid: string;
  final_pricing: string;
  payment_terms: string;
  payment_frequency: string;
  billing_contact_name: string;
  billing_contact_email: string;
  billing_cc_emails: string;
  billing_contact_phone: string;
  client_address_street: string;
  client_address_city: string;
  client_address_state: string;
  client_address_zip: string;
  // Square footage (BDD + AM)
  total_sqft: string;
  sterile_sqft: string;
  non_sterile_sqft: string;
  or_count: string;
  pr_count: string;
  // Management fee (mgmt-fee/kicker split 2026-07-07; toggle+prefill 2026-07-14).
  // Percent string ("3"), stored as a fraction on the facility. `mgmt_fee_on`
  // is the "Management fee?" toggle — defaults YES with a 3% prefill; toggle
  // OFF persists an explicit 0. Distinct from annual_kicker_pct (untouched).
  management_fee_pct: string;
  mgmt_fee_on: boolean;
  // AFB-0041 — '' | 'yes' | 'no'. '' persists as NULL (not answered).
  tcs_provides_usables: string;
  // Contractor (AM)
  am_eid: string;
  // Multiple service lines (2026-07-14). line[0] is the primary line whose
  // client price is the "Client Monthly Price" (final_pricing) above; extra
  // lines (day porter, monthly carpet, …) carry their own client_rate. Each
  // line captures its own sub pick + monthly/hourly cost.
  service_lines: ServiceLineForm[];
  no_miss_incentive: string;
  service_days_per_week: string;
  // Notes
  notes: string;
}

const EMPTY_FORM: FormState = {
  facility_name: '',
  account_id: '',
  account_parent_id: '',
  account_is_new: false,
  billing_account_id: '',
  account_name: '',
  facility_type: '',
  market_region: '',
  start_date: '',
  priority: 'Normal',
  bdd_eid: '',
  final_pricing: '',
  // Management fee defaults ON at 3% (editable). Toggle OFF → persist 0.
  management_fee_pct: DEFAULT_MGMT_FEE_PCT,
  mgmt_fee_on: true,
  // No default on purpose — an unanswered question must stay unanswered.
  tcs_provides_usables: '',
  payment_terms: '',
  payment_frequency: '',
  billing_contact_name: '',
  billing_contact_email: '',
  billing_cc_emails: '',
  billing_contact_phone: '',
  client_address_street: '',
  client_address_city: '',
  client_address_state: '',
  client_address_zip: '',
  total_sqft: '',
  sterile_sqft: '',
  non_sterile_sqft: '',
  or_count: '',
  pr_count: '',
  am_eid: '',
  service_lines: [emptyServiceLine()],
  no_miss_incentive: '',
  service_days_per_week: '',
  notes: '',
};

const AUTOSAVE_DEBOUNCE_MS = 1200;

// Use the shared helper; alias kept for call-site readability.
const numOrNull = (s: string): number | null => toNumOrNull(s) as number | null;
const str = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : String(n);

// Management-fee parse/format/persist helpers now live in ./mgmt-fee (pure +
// unit-tested): feePctToFraction, feeStateFromFraction, mgmtFeeToPersist.

// AFB-0019 — soft naming guardrail. Flags names that look like a person rather
// than a facility/center: exactly two Capitalized words AND no org keyword.
// Non-blocking — only drives an inline hint.
const NAS_ORG_KEYWORDS =
  /\b(Center|Centre|Surgery|Surgical|Health|Clinic|Dialysis|Medical|LLC|PLLC|Inc|Group|Partners)\b/i;
function looksLikePersonName(raw: string): boolean {
  const name = raw.trim();
  if (!name || NAS_ORG_KEYWORDS.test(name)) return false;
  const words = name.split(/\s+/);
  if (words.length !== 2) return false;
  return words.every((w) => /^[A-Z][a-z'’-]+$/.test(w));
}

// `known` is the loaded market vocabulary. Passing it in rather than reading a
// constant is the point: a draft is resumed against what the business runs
// TODAY, so a market added since the draft was saved resolves without a deploy.
function formFromDraft(d: NewAccountSop, known: string[]): FormState {
  const rawMarket = d.market_region ?? '';
  const canonicalMarket = known.includes(rawMarket)
    ? rawMarket
    : (NAS_LEGACY_MARKET_MAP[rawMarket] ?? '');
  return {
    facility_name: d.facility_name ?? '',
    account_id: d.account_id ?? '',
    account_parent_id: '',
    account_is_new: false,
    billing_account_id: '',
    account_name: d.account_name ?? '',
    facility_type: (d.facility_type as NewAccountSopFacilityType) ?? '',
    market_region: canonicalMarket,
    start_date: d.start_date ?? '',
    priority: (d.priority as NewAccountSopPriority) ?? 'Normal',
    bdd_eid: d.bdd_eid ?? '',
    final_pricing: str(d.final_pricing),
    // Resume the toggle + percent from the stored fraction: 0 → OFF, >0 → ON
    // + that %, unset(null) → ON + 3% default (matches a fresh EMPTY_FORM).
    management_fee_pct: feeStateFromFraction(d.management_fee_pct).pct,
    mgmt_fee_on: feeStateFromFraction(d.management_fee_pct).feeOn,
    tcs_provides_usables: toUsablesChoice(
      (d as { tcs_provides_usables?: boolean | null }).tcs_provides_usables ?? null,
    ),
    payment_terms: d.payment_terms ?? '',
    payment_frequency: d.payment_frequency ?? '',
    billing_contact_name: d.billing_contact_name ?? '',
    billing_contact_email: d.billing_contact_email ?? '',
    billing_cc_emails: d.billing_cc_emails ?? '',
    billing_contact_phone: d.billing_contact_phone ?? '',
    client_address_street: d.client_address_street ?? '',
    client_address_city: d.client_address_city ?? '',
    client_address_state: d.client_address_state ?? '',
    client_address_zip: d.client_address_zip ?? '',
    total_sqft: str(d.total_sqft),
    sterile_sqft: str(d.sterile_sqft),
    non_sterile_sqft: str(d.non_sterile_sqft),
    or_count: str(d.or_count),
    pr_count: str(d.pr_count),
    am_eid: d.am_eid ?? '',
    service_lines: serviceLinesFromRow(d),
    no_miss_incentive: str(d.no_miss_incentive),
    service_days_per_week: str(d.service_days_per_week),
    notes: d.notes ?? '',
  };
}

export default function SopCreateModal({
  open,
  onClose,
  onCreated,
  resumeDraft,
}: SopCreateModalProps) {
  const { employee } = useAuth();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bddOptions, setBddOptions] = useState<EmployeeOption[]>([]);
  const [amOptions, setAmOptions] = useState<EmployeeOption[]>([]);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const accountInputRef = useRef<HTMLInputElement | null>(null);

  // The market vocabulary, read from public.markets. One read, shared with
  // every other market surface.
  const { markets, error: marketsError } = useMarkets();
  const marketNames = useMemo(() => marketOptions(markets), [markets]);

  // Refused at ENTRY, before a card exists — the same rule the DB guard
  // enforces on the way in, not a second one. The Plano card was created with a
  // Colorado market on a Texas address and nothing anywhere said a word.
  const marketStateMismatch =
    form.market_region.trim() !== '' &&
    form.client_address_state.trim() !== '' &&
    !marketCoversState(markets, form.market_region, form.client_address_state);

  // The id of the draft row backing this session. Null until autosave (or
  // resume) creates/loads one. Persisting "Create account" flips is_draft.
  const draftIdRef = useRef<string | null>(null);
  // "Already on file?" (Ben, 2026-10-28): the subs this form has already made, so
  // a second press after a refusal never makes one twice, and the ones the person
  // said are a different company.
  const madeSubsRef = useRef(new Map<string, string>());
  const addAnywayRef = useRef(new Set<string>());
  const formRef = useRef<HTMLFormElement | null>(null);
  const [onFile, setOnFile] = useState<{ key: string; found: AlreadyOnFileError } | null>(null);
  const savingRef = useRef(false);
  const debounceRef = useRef<number | null>(null);

  // Reset / hydrate whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    if (resumeDraft) {
      setForm(formFromDraft(resumeDraft, marketNames));
      draftIdRef.current = resumeDraft.sop_id;
      setDraftSavedAt(resumeDraft.draft_last_saved_at ?? null);
    } else {
      setForm(EMPTY_FORM);
      draftIdRef.current = null;
      setDraftSavedAt(null);
    }
    setError(null);
    requestAnimationFrame(() => accountInputRef.current?.focus());
  }, [open, resumeDraft, marketNames]);

  // Load the eligibility-filtered BDD + AM pickers (#5). Two cheap queries.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const mapNames = (rows: { employee_id: string; employee_name_first: string | null; employee_name_last: string | null }[]) =>
        rows.map((r) => ({
          employee_id: r.employee_id,
          name:
            [r.employee_name_first, r.employee_name_last].filter(Boolean).join(' ').trim() ||
            r.employee_id,
        }));
      const [{ data: bdd }, { data: am }] = await Promise.all([
        // BDD half: v_employee_picker (TCSD-0152) — Active AND
        // pay_type='Salaried' AND app_role IS NOT NULL. PICKERS EXCLUDE,
        // RESOLVERS RESOLVE (TCSD-0129). ⛔ Do not widen this back to
        // v_employees_am, which is the FULL roster. employment_status is gone:
        // the view has no such column (naming one fails at RUNTIME, not at
        // build) and every row in it is Active by definition. Verified
        // 2026-09-02: all 5 is_bdd_eligible employees are inside the gate.
        supabase
          .from('v_employee_picker')
          .select('employee_id, employee_name_first, employee_name_last')
          .eq('is_bdd_eligible', true)
          .order('employee_name_first'),
        // AM half: v_am_assignable, not a hand-rolled is_am_eligible filter.
        // This surface already had the filter right; the point of repointing it
        // is that "who can be an AM" now has ONE definition, shared with the
        // facility-detail picker and with the write guard. A second hand-written
        // copy of a predicate is how the facility-detail picker drifted in the
        // first place. CW-L PR 3.
        supabase
          .from('v_am_assignable')
          .select('employee_id, employee_name_first, employee_name_last')
          .order('employee_name_first'),
      ]);
      if (cancelled) return;
      setBddOptions(mapNames(bdd ?? []));
      setAmOptions(mapNames(am ?? []));
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Canonical-only vocabulary: the dropdown can only yield a canonical metro
  // or empty, so the market resolves directly. Name kept so the two call
  // sites (buildPayload, createSubForBuilding) don't churn.
  const resolvedMarket = form.market_region;

  const setField = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      // No field is silently copied into another. The account name used to be
      // mirrored into card_title here, which is how card_title ended up
      // populated on every row and became the card's name by accident.
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Service-line repeater mutators. Patch one field on one line, add a blank
  // line, or drop a line (never below one — the first line is required).
  const setLine = useCallback(
    <K extends keyof ServiceLineForm>(
      index: number,
      key: K,
      value: ServiceLineForm[K],
    ) => {
      setForm((prev) => {
        const lines = prev.service_lines.map((ln, i) =>
          i === index ? { ...ln, [key]: value } : ln,
        );
        return { ...prev, service_lines: lines };
      });
    },
    [],
  );

  const addLine = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      service_lines: [...prev.service_lines, emptyServiceLine()],
    }));
  }, []);

  const removeLine = useCallback((index: number) => {
    setForm((prev) => {
      if (prev.service_lines.length <= 1) return prev;
      return {
        ...prev,
        service_lines: prev.service_lines.filter((_, i) => i !== index),
      };
    });
  }, []);

  // The card's money totals. The header "Client Monthly Price" reads this, so
  // a second line is IN the total the moment it is priced.
  const lineTotals = useMemo(
    () => serviceLineTotals(form.service_lines),
    [form.service_lines],
  );

  // Build the DB payload from the current form. `isDraft` toggles the draft
  // flag; populated-only so the audit log + row stay tidy.
  const buildPayload = useCallback(
    (isDraft: boolean, linesOverride?: ServiceLineForm[]): Record<string, unknown> => {
      const payload: Record<string, unknown> = {
        account_name: form.account_name.trim() || null,
        // The building, asked first and written first. This is what names the card.
        facility_name: form.facility_name.trim() || null,
        // The CHOSEN customer. materialize_new_account_sop honours this over any
        // lookup on account_name, so the picker's answer is the one that sticks and
        // the trigger never reaches its "no match" branch. Empty means the BDD
        // deliberately chose to create a customer, which the review step states.
        account_id: form.account_id.trim() || null,
        // card_title is NOT written. It is retired Trello residue kept read-only
        // for one release; the card's name comes from the facility. Deliberately
        // absent rather than set to null — an unconditional null is a writer that
        // has decided the column's meaning (TCSD-0072), and the legacy values are
        // still wanted for the alias/audit trail until the column is dropped.
        facility_type: form.facility_type || null,
        market_region: resolvedMarket || null,
        // ⛔ market_color is NOT sent. The colour lives on the market's own row
        // and a BEFORE trigger keeps the card's copy in step. The app writing it
        // is how one live card came to store Houston's dot on an Austin market.
        stage: 'Info_and_Sub_Checklist',
        status: 'Active',
        priority: form.priority,
        is_draft: isDraft,
      };
      if (isDraft) payload.draft_last_saved_at = new Date().toISOString();
      if (!draftIdRef.current) payload.created_by_eid = employee?.employee_id ?? null;

      // `set` skips empty/null text fields so they don't clutter the payload.
      // `setNum` ALWAYS writes numeric fields (even null) so a cleared number
      // overwrites the old DB value instead of being silently kept.
      const set = (k: string, v: unknown) => {
        if (v !== null && v !== undefined && v !== '') payload[k] = v;
      };
      const setNum = (k: string, v: number | null) => {
        payload[k] = v;
      };
      set('start_date', form.start_date || null);
      set('bdd_eid', form.bdd_eid || null);
      set('am_eid', form.am_eid || null);
      // Service lines (2026-07-14): the repeater is the full truth the
      // materialize trigger fans out. EVERY line carries its own client_rate,
      // including the first — the header total is derived from them, so there
      // is no longer a line whose price is typed somewhere else (2026-09-08).
      const effLines = linesOverride ?? form.service_lines;
      // ⛔ THE LINES ARE NOT WRITTEN ON THE CARD ANY MORE. They are recorded on
      // the BUILDING, through save_service_line(), once the card's first save
      // has created it — see the loop after the insert below. Writing them here
      // as well would put a second copy on the card for the seeder to fan out,
      // and then there would be two records of one fact again.
      //
      // final_pricing stays: it is the card's computed mirror of what the client
      // pays, the building derives its own rate from its lines by trigger, and
      // the card's window reads the building's figure first.
      setNum('final_pricing', serviceLineTotals(effLines).client);
      // ⛔ is_new_subcontractor is NOT written. Whether the sub is new is
      // answered by the sub on the building's line — a provider who has not
      // finished onboarding — not by a flag the card keeps for itself.
      // Management fee: toggle OFF → explicit 0; toggle ON → the entered % as a
      // fraction, defaulting to 3% when left blank. Always a number, never null.
      setNum('management_fee_pct', mgmtFeeToPersist(form.mgmt_fee_on, form.management_fee_pct));
      // AFB-0041 — always written, including NULL, so clearing the answer in a
      // draft really does clear it (same reason setNum always writes).
      payload.tcs_provides_usables = fromUsablesChoice(form.tcs_provides_usables);
      set('payment_terms', form.payment_terms.trim() || null);
      set('payment_frequency', form.payment_frequency.trim() || null);
      // ⛔ no_miss_incentive is NOT written on the card. The no-miss bonus is a
      // term of the deal with a SUB, so it belongs on the line that names the
      // sub — no_misses_bonus_enabled / no_misses_bonus_amount, which the
      // Contractor tab has always edited and payroll has always read. The
      // figure typed at intake rides onto the first line in the save_service_line
      // loop below, so the number the card shows is the number being paid.
      setNum('service_days_per_week', numOrNull(form.service_days_per_week));
      setNum('total_sqft', numOrNull(form.total_sqft));
      setNum('sterile_sqft', numOrNull(form.sterile_sqft));
      setNum('non_sterile_sqft', numOrNull(form.non_sterile_sqft));
      setNum('or_count', numOrNull(form.or_count));
      setNum('pr_count', numOrNull(form.pr_count));
      set('billing_contact_name', form.billing_contact_name.trim() || null);
      set('billing_contact_email', form.billing_contact_email.trim() || null);
      set('billing_cc_emails', form.billing_cc_emails.trim() || null);
      set('billing_contact_phone', form.billing_contact_phone.trim() || null);
      set('client_address_street', form.client_address_street.trim() || null);
      set('client_address_city', form.client_address_city.trim() || null);
      set('client_address_state', form.client_address_state.trim() || null);
      set('client_address_zip', form.client_address_zip.trim() || null);
      // ⛔ THE SUB'S NAME AND NUMBER ARE NOT MIRRORED ONTO THE CARD.
      // They were copied here so the card header and the onboarding views could
      // show the primary sub. Those surfaces read the card's window now, which
      // resolves the name and the number from the building's own service line —
      // so the copy had no reader and two places to go wrong.
      set('notes', form.notes.trim() || null);
      return payload;
    },
    [form, resolvedMarket, employee],
  );

  // Count meaningfully-filled fields → autosave threshold (#4). Excludes the
  // priority/sub_mode defaults so an untouched form never autosaves.
  const filledCount = useMemo(() => {
    let n = 0;
    for (const [k, v] of Object.entries(form)) {
      if (k === 'priority') continue;
      if (typeof v === 'string' && v.trim() !== '') n += 1;
    }
    // Meaningful service lines count toward the autosave threshold too.
    n += form.service_lines.filter(isMeaningfulLine).length;
    return n;
  }, [form]);

  const persistDraft = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      const payload = buildPayload(true);
      if (!draftIdRef.current) {
        // Seed the checklist on first insert so the resumed/converted row is
        // consistent with create-flow rows.
        const { data: checklist } = await supabase.rpc('nas_derive_checklist', {
          p_stage: 'Info_and_Sub_Checklist',
        });
        payload.checklist = checklist ?? {};
        const { data, error: e } = await supabase
          .from('new_account_sop')
          .insert(payload)
          .select('sop_id')
          .single();
        if (e) throw e;
        draftIdRef.current = data?.sop_id ?? null;
      } else {
        const { error: e } = await supabase
          .from('new_account_sop')
          .update(payload)
          .eq('sop_id', draftIdRef.current);
        if (e) throw e;
      }
      setDraftSavedAt(new Date().toISOString());
    } catch (e) {
      console.warn('[sop-create] draft autosave failed', e);
    } finally {
      savingRef.current = false;
    }
  }, [buildPayload]);

  // Debounced autosave: fires once ~4 fields are filled and account_name is set.
  useEffect(() => {
    if (!open) return;
    if (form.account_name.trim().length === 0 || filledCount < 4) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void persistDraft();
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [open, form, filledCount, persistDraft]);

  // ---- site-first wizard state -------------------------------------------------
  const [step, setStep] = useState<IntakeStep>('building');
  const [customerChoice, setCustomerChoice] = useState<CustomerChoice | null>(null);
  const [parentOptions, setParentOptions] = useState<{ parent_id: string; parent_name: string }[]>([]);
  const [billToOptions, setBillToOptions] = useState<{ account_id: string; account_name: string }[]>([]);

  // Reset to step 1 whenever the modal opens, so a reopened form never resumes
  // halfway through a question the BDD has not seen.
  useEffect(() => {
    if (open) setStep('building');
  }, [open]);

  // facilities.account_id has NO foreign key (PLAYBOOK §2), so PostgREST cannot embed
  // through it. Both of these are flat fetches for that reason.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      const [parents, accts] = await Promise.all([
        supabase.from('corporate_parents').select('parent_id, parent_name').order('parent_name'),
        supabase
          .from('accounts')
          .select('account_id, account_name')
          .neq('account_status', 'Closed')
          .order('account_name'),
      ]);
      if (cancelled) return;
      setParentOptions((parents.data ?? []) as { parent_id: string; parent_name: string }[]);
      setBillToOptions((accts.data ?? []) as { account_id: string; account_name: string }[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const onCustomerChange = useCallback((c: CustomerChoice | null) => {
    setCustomerChoice(c);
    setForm((prev) => ({
      ...prev,
      account_id: c?.account_id ?? '',
      account_name: c?.account_name ?? '',
      account_parent_id: c?.parent_id ?? '',
      account_is_new: c?.isNew ?? false,
    }));
  }, []);

  /** The two site-first steps, as the pure model sees them. */
  const intakeAnswers = useMemo(
    () => ({
      facility_name: form.facility_name,
      client_address_street: form.client_address_street,
      client_address_city: form.client_address_city,
      client_address_state: form.client_address_state,
      client_address_zip: form.client_address_zip,
      facility_type: form.facility_type,
      market_region: form.market_region,
      account_id: form.account_id,
      account_name: form.account_name,
    }),
    [form],
  );

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    // The building and the customer are both required now — that is the whole point
    // of the rebuild. The old minimum (AFB-0013) was account name + facility type,
    // which is how a card reached the board with no building on it.
    if (!canAdvance('building', intakeAnswers)) return false;
    if (!canAdvance('customer', intakeAnswers)) return false;
    // Each authored service line must be internally valid: a "previous" pick
    // needs a linked sub; a "new" sub needs a go-by name.
    for (const ln of form.service_lines) {
      if (ln.sub_mode === 'previous' && !ln.service_provider_id) return false;
      if (ln.sub_mode === 'new' && ln.contractor_name.trim().length === 0) return false;
    }
    // Management fee: when the toggle is ON a valid non-negative percent is
    // required (prefilled to 3%, so satisfied by default). Toggle OFF → 0, fine.
    if (form.mgmt_fee_on && feePctToFraction(form.management_fee_pct) === null) return false;
    // ⛔ A market that does not cover the building's state is refused HERE, at
    // entry, before a card exists. The DB guard says the same thing on the way
    // in; this is the same rule shown early, not a second one.
    if (marketStateMismatch) return false;
    return true;
  }, [form, submitting, intakeAnswers, marketStateMismatch]);

  /** What is still missing on THIS step, in words. Empty = the Next button works. */
  const blocking = useMemo(() => blockingReasons(step, intakeAnswers), [step, intakeAnswers]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setOnFile(null);
    let subKey: string | null = null;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    try {
      // #8 — each NEW sub line goes through the one canonical onboarding path;
      // we link the freshly-minted provider_id back onto that line, then let
      // buildPayload serialize the resolved lines + mirror line[0].
      const resolvedLines: ServiceLineForm[] = [];
      for (const ln of form.service_lines) {
        if (ln.sub_mode === 'new' && ln.contractor_name.trim().length > 0) {
          subKey = newSubKey(ln.contractor_name, ln.contractor_phone);
          let providerId = madeSubsRef.current.get(subKey);
          if (!providerId) {
            const res = await createSubForBuilding({
              name: ln.contractor_name,
              legalBusinessName: ln.contractor_legal_name.trim() || null,
              phone: ln.contractor_phone,
              email: ln.contractor_email,
              marketRegion: resolvedMarket || null,
              createdByEid: employee?.employee_id ?? null,
              addAnyway: addAnywayRef.current.has(subKey),
            });
            providerId = res.providerId;
            madeSubsRef.current.set(subKey, providerId);
          }
          subKey = null;
          resolvedLines.push({ ...ln, service_provider_id: providerId });
        } else {
          resolvedLines.push(ln);
        }
      }

      const payload = buildPayload(false, resolvedLines);

      let sopId = draftIdRef.current;
      let accountName = form.account_name.trim();

      if (draftIdRef.current) {
        const { data, error: e } = await supabase
          .from('new_account_sop')
          .update(payload)
          .eq('sop_id', draftIdRef.current)
          .select('sop_id, account_name')
          .single();
        if (e) throw e;
        sopId = data?.sop_id ?? draftIdRef.current;
        accountName = data?.account_name ?? accountName;
      } else {
        const { data: checklist } = await supabase.rpc('nas_derive_checklist', {
          p_stage: 'Info_and_Sub_Checklist',
        });
        payload.checklist = checklist ?? {};
        const { data, error: e } = await supabase
          .from('new_account_sop')
          .insert(payload)
          .select('sop_id, account_name')
          .single();
        if (e) throw e;
        sopId = data?.sop_id ?? null;
        accountName = data?.account_name ?? accountName;
      }

      // Bill-to lands on the FACILITY, and the facility only exists once
      // materialize_new_account_sop has run on the row we just wrote — so read the
      // facility back rather than assuming one. billing_account_id is not a protected
      // column, so this is a plain update. Only written when it actually differs;
      // "same as customer" stays NULL rather than duplicating account_id, which is why
      // the column reads as 3 rows and not 209.
      const billTo = form.billing_account_id.trim();
      if (sopId && billTo && billTo !== form.account_id.trim()) {
        const { data: row } = await supabase
          .from('new_account_sop')
          .select('facility_id')
          .eq('sop_id', sopId)
          .single();
        if (row?.facility_id) {
          const { error: billErr } = await supabase
            .from('facilities')
            .update({ billing_account_id: billTo })
            .eq('facility_id', row.facility_id);
          // A failed bill-to must not lose the card that was just created — surface
          // it and carry on, rather than throwing into the catch below.
          if (billErr) {
            console.error('[sop-create] bill-to not saved', billErr);
            setError(
              `The card was created, but the bill-to could not be saved: ${rpcErrorMessage(billErr, 'unknown error')}. Set it on the facility.`,
            );
          }
        }
      }

      // ⛔ THE LINES GO ON THE BUILDING, AND THE BUILDING EXISTS BY NOW. The
      // card's first save creates it (a contract is what starts onboarding), so
      // the facility is read back rather than assumed — the same shape the
      // bill-to above uses. Each line then goes through the one door, which
      // mints its id under a lock and leaves the status word to the dates.
      //
      // ⛔ A line that does not land must not silently vanish behind a created
      // card. The card is kept and the failure is said out loud.
      const keptLines = resolvedLines.filter(isMeaningfulLine);
      if (sopId && keptLines.length > 0) {
        const { data: facRow } = await supabase
          .from('new_account_sop')
          .select('facility_id')
          .eq('sop_id', sopId)
          .single();
        if (facRow?.facility_id) {
          // The no-miss bonus typed at intake is one figure for the building,
          // and the building records it per line. It goes on the FIRST line —
          // the one that names the sub doing the main clean — so the total the
          // card shows is the same number, and the Contractor tab can move or
          // split it afterwards. Only sent when it is actually a figure: a
          // blank must not switch the bonus on at zero.
          const noMiss = numOrNull(form.no_miss_incentive);
          for (const [i, line] of keptLines.entries()) {
            const patch = serviceLineToPatch(line);
            if (i === 0 && noMiss !== null && noMiss > 0) {
              patch.no_misses_bonus_enabled = true;
              patch.no_misses_bonus_amount = noMiss;
            }
            const { error: lineErr } = await supabase.rpc('save_service_line', {
              p_facility_id: facRow.facility_id,
              p_patch: patch,
              p_assignment_id: null,
              p_sop_id: sopId,
            });
            if (lineErr) {
              console.error('[sop-create] service line not saved', lineErr);
              setError(
                `The card was created, but a service line could not be saved: ${rpcErrorMessage(lineErr, 'unknown error')}. Add it on the Contractor tab.`,
              );
            }
          }
        }
      }

      onCreated?.(sopId ?? '', accountName);
      setForm(EMPTY_FORM);
      draftIdRef.current = null;
      setDraftSavedAt(null);
      onClose();
    } catch (err) {
      const found = alreadyOnFileFrom(err);
      if (found && subKey) {
        setOnFile({ key: subKey, found });
        return;
      }
      console.error('[sop-create] create failed', err);
      // PostgrestError is a plain object, not an Error — rpcErrorMessage
      // (shared house helper) surfaces the real DB message instead of the
      // generic banner; append the DB hint when the backend provides one.
      const msg = rpcErrorMessage(
        err,
        'Could not create account. Try again, and if it persists tell the COO.',
      );
      const hint =
        err && typeof err === 'object' && typeof (err as { hint?: unknown }).hint === 'string' && (err as { hint: string }).hint
          ? (err as { hint: string }).hint
          : null;
      setError(hint ? `${msg} — ${hint}` : msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const marketDot = form.market_region
    ? marketColorPair(markets, form.market_region).base
    : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[96vh] w-full flex-col overflow-hidden rounded-t-xl bg-tcs-overlay shadow-xl sm:max-h-[90vh] sm:w-[600px] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sop-create-title"
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-tcs-border bg-tcs-bg p-4">
          <div>
            <h2 id="sop-create-title" className="text-base font-semibold text-tcs-text">
              {resumeDraft ? 'Resume draft account' : 'New account'}
            </h2>
            <p className="mt-0.5 text-xs text-tcs-text-secondary">
              Starts in <span className="font-medium">Info &amp; Sub Checklist</span>.{' '}
              {draftSavedAt ? (
                <span className="text-rag-green">
                  Draft saved {new Date(draftSavedAt).toLocaleTimeString()}
                </span>
              ) : (
                <>SOP&nbsp;ID auto-assigned on save.</>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border bg-tcs-raised p-1.5 text-tcs-text-secondary hover:bg-tcs-bg-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <StepRail items={stepRail(step)} />

        <form ref={formRef} onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            {/* The question at the top of whichever step we are on. Step 2's heading
                interpolates the building — that is the contrast device, not decoration. */}
            <div className="mb-4">
              <h3 className="m-0 text-base font-semibold text-tcs-text">
                {stepHeading(step, form.facility_name)}
              </h3>
              <p className="m-0 mt-0.5 text-xs text-tcs-text-muted">{stepSubheading(step)}</p>
            </div>

            {/* ===================== STEP 1 — the building ===================== */}
            {step === 'building' && (
              <div>
                <IntakeField
                  label="Facility name"
                  required
                  ask={FACILITY_NAME_HELP.ask}
                  help={
                    <FieldHelp
                      body={FACILITY_NAME_HELP.body}
                      good={FACILITY_NAME_HELP.good}
                      bad={FACILITY_NAME_HELP.bad}
                    />
                  }
                >
                  <input
                    ref={accountInputRef}
                    type="text"
                    value={form.facility_name}
                    onChange={(e) => setField('facility_name', e.target.value)}
                    placeholder="e.g. DaVita — Live Oak (San Antonio)"
                    className={INTAKE_INPUT_CLS}
                    autoComplete="off"
                  />
                  {looksLikePersonName(form.facility_name) && (
                    <span className="mt-1 block text-xs text-st-warn-fg" role="status">
                      Use the building&apos;s name, not a contact person.
                    </span>
                  )}
                </IntakeField>

                <IntakeField label="Street address" required>
                  <input
                    type="text"
                    value={form.client_address_street}
                    onChange={(e) => setField('client_address_street', e.target.value)}
                    className={INTAKE_INPUT_CLS}
                    autoComplete="off"
                  />
                </IntakeField>

                <div className="grid gap-3 sm:grid-cols-[2fr_80px_100px]">
                  <IntakeField label="City" required>
                    <input
                      type="text"
                      value={form.client_address_city}
                      onChange={(e) => setField('client_address_city', e.target.value)}
                      className={INTAKE_INPUT_CLS}
                      autoComplete="off"
                    />
                  </IntakeField>
                  <IntakeField label="State" required>
                    <input
                      type="text"
                      value={form.client_address_state}
                      onChange={(e) => setField('client_address_state', e.target.value)}
                      className={INTAKE_INPUT_CLS}
                      maxLength={2}
                      autoComplete="off"
                    />
                  </IntakeField>
                  <IntakeField label="ZIP" required>
                    <input
                      type="text"
                      value={form.client_address_zip}
                      onChange={(e) => setField('client_address_zip', e.target.value)}
                      className={INTAKE_INPUT_CLS}
                      autoComplete="off"
                    />
                  </IntakeField>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <IntakeField label="Facility type" required>
                    <select
                      value={form.facility_type}
                      onChange={(e) =>
                        setField('facility_type', e.target.value as NewAccountSopFacilityType | '')
                      }
                      className={INTAKE_INPUT_CLS}
                    >
                      <option value="">Choose one…</option>
                      {NAS_FACILITY_TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {NAS_FACILITY_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </select>
                  </IntakeField>
                  <IntakeField label="Market" required>
                    <div className="flex items-center gap-2">
                      {marketDot && (
                        <span
                          className="inline-block h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: marketDot }}
                          aria-hidden="true"
                        />
                      )}
                      <select
                        value={form.market_region}
                        onChange={(e) => setField('market_region', e.target.value)}
                        className={INTAKE_INPUT_CLS}
                      >
                        <option value="">Choose one…</option>
                        {marketOptions(markets).map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>
                    {marketsError && (
                      <p className="mt-1 text-xs text-tcs-text-muted">
                        The market list could not be loaded, so this dropdown is
                        empty. Reload rather than saving without a market.
                      </p>
                    )}
                    {marketStateMismatch && (
                      <p className="mt-1 text-xs text-st-danger-fg-strong">
                        {MARKET_STATE_MISMATCH_HINT(
                          form.client_address_state.trim().toUpperCase(),
                          form.market_region,
                        )}
                      </p>
                    )}
                  </IntakeField>
                </div>
              </div>
            )}

            {/* ============= STEP 2 — who signs, and who pays ============= */}
            {step === 'customer' && (
              <div>
                <IntakeField
                  label="Customer"
                  required
                  ask={CUSTOMER_HELP.ask}
                  help={
                    <FieldHelp
                      body={CUSTOMER_HELP.body}
                      good={CUSTOMER_HELP.good}
                      bad={CUSTOMER_HELP.bad}
                    />
                  }
                >
                  <CustomerPicker
                    value={customerChoice}
                    parentOptions={parentOptions}
                    disabled={submitting}
                    onChange={onCustomerChange}
                  />
                </IntakeField>

                <IntakeField
                  label="Bill-to"
                  ask={BILL_TO_HELP.ask}
                  help={
                    <FieldHelp
                      body={BILL_TO_HELP.body}
                      good={[BILL_TO_HELP.example]}
                      bad={[]}
                    />
                  }
                >
                  <select
                    value={form.billing_account_id}
                    onChange={(e) => setField('billing_account_id', e.target.value)}
                    className={INTAKE_INPUT_CLS}
                  >
                    <option value="">
                      Same as customer{form.account_name ? ` — ${form.account_name}` : ''}
                    </option>
                    {billToOptions.map((o) => (
                      <option key={o.account_id} value={o.account_id}>
                        {o.account_name}
                      </option>
                    ))}
                  </select>
                </IntakeField>

                <IntakeField label="Billing contact">
                  <div className="grid gap-3 sm:grid-cols-3">
                    <input type="text" value={form.billing_contact_name} onChange={(e) => setField('billing_contact_name', e.target.value)} className={INTAKE_INPUT_CLS} placeholder="Name" autoComplete="off" />
                    <input type="email" value={form.billing_contact_email} onChange={(e) => setField('billing_contact_email', e.target.value)} className={INTAKE_INPUT_CLS} placeholder="Email" autoComplete="off" />
                    <input type="tel" value={form.billing_contact_phone} onChange={(e) => setField('billing_contact_phone', e.target.value)} className={INTAKE_INPUT_CLS} placeholder="Phone" autoComplete="off" />
                  </div>
                </IntakeField>
              </div>
            )}

            {/* ===================== STEP 5 — review ===================== */}
            {step === 'review' && (
              <div className="overflow-hidden rounded-md border border-tcs-border">
                <ReviewRow
                  label="Facility name"
                  value={form.facility_name}
                  note="This is the name that appears on the pipeline card, in My Work, and on AM routes."
                  highlight
                />
                <ReviewRow
                  label="Address"
                  value={[form.client_address_street, form.client_address_city, `${form.client_address_state} ${form.client_address_zip}`.trim()].filter(Boolean).join(', ')}
                />
                <ReviewRow
                  label="Customer"
                  value={form.account_name || '—'}
                  note={
                    customerChoice && !customerChoice.isNew
                      ? `Existing · ${form.account_id}`
                      : 'New customer — will be created'
                  }
                />
                <ReviewRow
                  label="Bill-to"
                  value={
                    form.billing_account_id
                      ? (billToOptions.find((o) => o.account_id === form.billing_account_id)?.account_name ?? form.billing_account_id)
                      : 'Same as customer'
                  }
                />
                <ReviewRow
                  label="Facility type · Market"
                  value={[form.facility_type, form.market_region].filter(Boolean).join(' · ') || '—'}
                />
                <ReviewRow
                  label="Management fee"
                  value={form.mgmt_fee_on ? `${form.management_fee_pct}%` : '0% — waived'}
                  note="Finance or the BDD adjusts during setup if waived."
                />
              </div>
            )}

            {/* ============ STEPS 3 & 4 — service, pricing, subcontractor ============ */}
            <div className={step === 'service' || step === 'subcontractor' ? '' : 'hidden'}>
            {/* ---- Client & Billing (BDD) ---- */}
            <SectionHeading title="Client & Billing" owner="BDD" />
            <div className="grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <EmployeeField label="BDD" value={form.bdd_eid} onChange={(v) => setField('bdd_eid', v)} options={bddOptions} placeholder="Unassigned" />
                {/* The total is the SUM of the service lines below, never a
                    figure typed beside them. Two cards were opened for one
                    Houston hospital because the header held one line's price
                    and the second line's price lived somewhere else. */}
                <Field
                  label="Client Monthly Price ($)"
                  hint="Adds up the service lines below. Price each line there."
                >
                  <input
                    type="text"
                    value={lineTotals.client != null ? lineTotals.client.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                    readOnly
                    aria-readonly="true"
                    className={`${INPUT_CLS} bg-tcs-bg-muted font-mono`}
                    placeholder="—"
                  />
                </Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Payment terms"><input type="text" value={form.payment_terms} onChange={(e) => setField('payment_terms', e.target.value)} className={INPUT_CLS} placeholder="e.g. Net 30" autoComplete="off" /></Field>
                <Field label="Payment frequency"><input type="text" value={form.payment_frequency} onChange={(e) => setField('payment_frequency', e.target.value)} className={INPUT_CLS} placeholder="e.g. Monthly" autoComplete="off" /></Field>
                {/* Management fee (mgmt-fee/kicker split 2026-07-07; toggle+
                    prefill 2026-07-14). "Management fee?" toggle defaults YES
                    with a 3% prefill; toggle OFF persists 0. Captured at intake,
                    carried to facilities.management_fee_pct on materialization.
                    Distinct from annual_kicker_pct. */}
                <div className="sm:col-span-2">
                  <Field
                    label="Management fee?"
                    hint="Applies a monthly management fee to this account. Defaults to 3% — editable, or switch off for no fee (saves as 0). Distinct from the annual kicker."
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-tcs-text">
                        <input
                          type="checkbox"
                          checked={form.mgmt_fee_on}
                          onChange={(e) => {
                            const on = e.target.checked;
                            setField('mgmt_fee_on', on);
                            // Prefill 3% when turning the fee on with a blank field.
                            if (on && form.management_fee_pct.trim() === '') {
                              setField('management_fee_pct', DEFAULT_MGMT_FEE_PCT);
                            }
                          }}
                          className="h-4 w-4 rounded border-tcs-border"
                        />
                        Apply management fee
                      </label>
                      {form.mgmt_fee_on ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number" inputMode="decimal" step="0.01" min="0" max="99.99"
                            value={form.management_fee_pct}
                            onChange={(e) => setField('management_fee_pct', e.target.value)}
                            aria-label="Management fee percent"
                            className={`${INPUT_CLS} w-28`}
                            placeholder="e.g. 3"
                          />
                          <span className="text-sm text-tcs-text-secondary">%</span>
                          <span className="ml-1 text-xs text-tcs-text-muted">default 3%</span>
                        </div>
                      ) : (
                        <span className="text-xs text-tcs-text-muted">No management fee (saves as 0).</span>
                      )}
                    </div>
                  </Field>
                </div>
                {/* AFB-0041 (Jose) — usables captured at intake. Deliberately
                    NOT defaulted: 157 of 196 Active facilities are unknown
                    precisely because nothing ever asked, and a default would
                    turn "never asked" into a confident answer. "Not answered
                    yet" is a valid, visible state that the completeness
                    scorecard counts against us until someone resolves it. */}
                <div className="sm:col-span-2">
                  <Field
                    label="Usables account?"
                    hint="Does TCS supply usables (liners, paper, chemicals)? Leave unanswered if you don't know — it will show as 'Not set' on the facility and on the data-completeness scorecard rather than defaulting to No."
                  >
                    <select
                      value={form.tcs_provides_usables}
                      onChange={(e) => setField('tcs_provides_usables', e.target.value)}
                      aria-label="Usables account"
                      className={INPUT_CLS}
                    >
                      {USABLES_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Start date"><input type="date" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} className={INPUT_CLS} /></Field>
                {/* #4 — Service days/wk is a client service/billing term; lives in Client & Billing, not Contractor. */}
                <Field label="Service days / week"><input type="number" inputMode="numeric" min="1" max="7" value={form.service_days_per_week} onChange={(e) => setField('service_days_per_week', e.target.value)} className={INPUT_CLS} placeholder="5" /></Field>
              </div>
              {/* The billing contact and the building's address moved to steps 2 and 1.
                  They are not repeated here — two inputs bound to one value is how a form
                  starts disagreeing with itself. */}
              <Field label="Additional / CC billing emails" hint="Comma-separated. Extra recipients copied on invoices.">
                <input type="text" value={form.billing_cc_emails} onChange={(e) => setField('billing_cc_emails', e.target.value)} className={INPUT_CLS} placeholder="ap@client.com, controller@client.com" autoComplete="off" />
              </Field>
            </div>

            {/* ---- Square Footage (BDD + AM) ---- */}
            <SectionHeading title="Square Footage" owner="BDD + AM" />
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Total sq ft"><input type="number" inputMode="numeric" min="0" value={form.total_sqft} onChange={(e) => setField('total_sqft', e.target.value)} className={INPUT_CLS} /></Field>
              <Field label="Sterile sq ft" hint="Procedure / endoscopy areas."><input type="number" inputMode="numeric" min="0" value={form.sterile_sqft} onChange={(e) => setField('sterile_sqft', e.target.value)} className={INPUT_CLS} /></Field>
              <Field label="Non-sterile sq ft"><input type="number" inputMode="numeric" min="0" value={form.non_sterile_sqft} onChange={(e) => setField('non_sterile_sqft', e.target.value)} className={INPUT_CLS} /></Field>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="OR count" hint="Operating rooms."><input type="number" inputMode="numeric" min="0" value={form.or_count} onChange={(e) => setField('or_count', e.target.value)} className={INPUT_CLS} /></Field>
              <Field label="PR count" hint="Procedure rooms."><input type="number" inputMode="numeric" min="0" value={form.pr_count} onChange={(e) => setField('pr_count', e.target.value)} className={INPUT_CLS} /></Field>
            </div>

            {/* ---- Contractor (AM) ---- */}
            <SectionHeading title="Contractor" owner="AM" />
            <div className="grid gap-3">
              <EmployeeField label="AM" value={form.am_eid} onChange={(v) => setField('am_eid', v)} options={amOptions} placeholder="Unassigned" />

              {/* Service lines: one card per line, each with its own price,
                  sub and cost. The header total adds them up.

                  ⛔ The heading and the sentence under it are the fix for a
                  MEASURED failure, not decoration. The "+ Add service line"
                  button already existed and the person who needed it did not
                  find it: they opened a SECOND onboarding card for the same
                  Houston hospital, one for the nightly crew and one for the
                  day porter. Nothing on this form said that a building can be
                  described as more than one kind of work. Now it says so
                  before the first line is filled in, in the words a BDD would
                  use — not "line", which is a database word. */}
              <div className="grid gap-3">
                <div className="rounded-md border border-tcs-border bg-tcs-bg/40 px-3 py-2">
                  <p className="text-sm font-medium text-tcs-text">
                    What are we doing at this building?
                  </p>
                  <p className="mt-1 text-xs text-tcs-text-secondary">
                    One entry per kind of work. A nightly crew and a day porter at the
                    same building are <strong>two entries on this one card</strong> — not
                    two cards. Add carpet, floors or a stripping crew the same way.
                  </p>
                </div>
                {form.service_lines.map((line, i) => (
                  <ServiceLineCard
                    key={i}
                    index={i}
                    line={line}
                    onField={(k, v) => setLine(i, k, v)}
                    onRemove={() => removeLine(i)}
                    canRemove={form.service_lines.length > 1}
                  />
                ))}
                <button
                  type="button"
                  onClick={addLine}
                  className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-md border border-dashed border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-text hover:bg-tcs-bg-muted"
                >
                  <Plus className="h-4 w-4" /> Add another kind of work at this building
                </button>
              </div>

              {/* #4 — "Service days / week" moved up to Client & Billing. */}
              <Field label="Scope Performance Payment ($)"><input type="number" inputMode="decimal" step="0.01" min="0" value={form.no_miss_incentive} onChange={(e) => setField('no_miss_incentive', e.target.value)} className={INPUT_CLS} placeholder="0.00" /></Field>
            </div>

            {/* ---- Notes ---- */}
            <SectionHeading title="Notes" />
            <Field label="Notes">
              <textarea value={form.notes} onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setField('notes', e.target.value)} className={`${INPUT_CLS} min-h-[80px]`} rows={3} />
            </Field>
            </div>
            {/* ============ end steps 3 & 4 ============ */}

            {blocking.length > 0 && (
              <ul className="mt-3 rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg">
                {blocking.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}

            {error && (
              <div className="mt-3 rounded-md border border-rag-red bg-st-danger-bg px-3 py-2 text-xs text-rag-red" role="alert">
                {error}
              </div>
            )}
            {onFile && (
              <div className="mt-3">
                <AlreadyOnFileNotice
                  found={onFile.found}
                  busy={submitting}
                  openInNewTab
                  instead="If it's them, pick them from the sub list on that service line instead of adding a new sub."
                  onAddAnyway={() => {
                    addAnywayRef.current.add(onFile.key);
                    setOnFile(null);
                    formRef.current?.requestSubmit();
                  }}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <footer className="flex items-center justify-between gap-2 border-t border-tcs-border bg-tcs-bg px-4 py-3">
            <span className="text-[11px] text-tcs-text-secondary">
              {step === 'review'
                ? reviewFooterSentence(intakeAnswers)
                : draftSavedAt
                  ? 'Saved as draft — safe to close.'
                  : 'Autosaves as you type.'}
            </span>
            <div className="flex items-center gap-2">
              {step === 'building' ? (
                <button type="button" onClick={onClose} disabled={submitting} className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50">
                  Cancel
                </button>
              ) : (
                <button type="button" onClick={() => setStep(prevStep(step))} disabled={submitting} className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50">
                  Back
                </button>
              )}

              {step === 'review' ? (
                <button type="submit" disabled={!canSubmit} className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:cursor-not-allowed disabled:opacity-50">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {submitting ? 'Creating…' : 'Create account card'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setStep(nextStep(step))}
                  disabled={blocking.length > 0 || submitting}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {step === 'building' ? 'Next: who signs' : 'Next'}
                </button>
              )}
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Building blocks. 16px inputs (in INPUT_CLS) keep iOS Safari from zooming.
// ----------------------------------------------------------------------------

function SectionHeading({ title, owner }: { title: string; owner?: string }) {
  return (
    <div className="mt-6 mb-2 flex items-center justify-between border-b border-tcs-border pb-1">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-tcs-text">{title}</h3>
      {owner && (
        <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
          {owner}
        </span>
      )}
    </div>
  );
}

function EmployeeField({
  label,
  value,
  onChange,
  options,
  placeholder = 'Unassigned',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: EmployeeOption[];
  placeholder?: string;
}) {
  return (
    <Field label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={INPUT_CLS}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.employee_id} value={o.employee_id}>
            {o.name}
          </option>
        ))}
      </select>
    </Field>
  );
}
